import { type ReactNode, useEffect, useState } from 'react';
import { Image, type ImageStyle, type StyleProp } from 'react-native';

/**
 * First-page PDF thumbnail (Wave 52) — web implementation. pdf.js
 * renders page 1 to a canvas once, the JPEG data-URL is cached for the
 * session, and every tile / attached-doc row showing the same file
 * reuses it. pdf.js is dynamically imported so the library only loads
 * when a PDF tile is actually on screen. The native variant
 * (PdfThumb.tsx) renders the fallback icon — PDF rasterization there
 * would need a native module.
 */

// uri → data-URL (or in-flight promise; failures evict themselves).
const thumbCache = new Map<string, string | Promise<string>>();

const THUMB_WIDTH = 480;

async function renderFirstPage(uri: string): Promise<string> {
  // v3 UMD build on purpose — v4+ is ESM with import.meta, which Metro
  // can't bundle. Pinned in package.json; don't bump past 3.x.
  // STATIC import (not `await import(...)`): a dynamic import makes
  // Metro's web export emit an async chunk, which renumbers module ids
  // and corrupts the separately-built expo-sqlite worker bundle
  // ("Requiring unknown module") — that broke the whole web app's DB
  // boot in the first Wave 52 deploy.
  const pdfjs = require('pdfjs-dist') as typeof import('pdfjs-dist');
  // Cross-origin worker: pdf.js falls back to a main-thread "fake
  // worker" if the browser refuses it — fine for one-page thumbnails.
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
  }
  const doc = await pdfjs.getDocument({ url: uri }).promise;
  try {
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: THUMB_WIDTH / base.width });
    // eslint-disable-next-line no-undef
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    // Crop to the top of the page — tiles are roughly square, and the
    // letterhead/header is what identifies a document at a glance
    // (resizeMode "cover" would otherwise center-crop to mid-page).
    canvas.height = Math.min(Math.ceil(viewport.height), Math.ceil(THUMB_WIDTH * 1.15));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport } as never).promise;
    return canvas.toDataURL('image/jpeg', 0.82);
  } finally {
    void doc.destroy();
  }
}

function getThumb(uri: string): string | Promise<string> {
  const hit = thumbCache.get(uri);
  if (hit) return hit;
  const p = renderFirstPage(uri).then(
    (dataUrl) => {
      thumbCache.set(uri, dataUrl);
      return dataUrl;
    },
    (err) => {
      thumbCache.delete(uri);
      console.warn('pdf thumbnail failed', err);
      throw err;
    }
  );
  thumbCache.set(uri, p);
  return p;
}

export function PdfThumb({
  uri,
  style,
  fallback,
}: {
  uri: string;
  style?: StyleProp<ImageStyle>;
  fallback: ReactNode;
}) {
  const cached = thumbCache.get(uri);
  const [thumb, setThumb] = useState<string | null>(
    typeof cached === 'string' ? cached : null
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (thumb) return;
    let alive = true;
    const v = getThumb(uri);
    if (typeof v === 'string') {
      setThumb(v);
      return;
    }
    v.then(
      (dataUrl) => {
        if (alive) setThumb(dataUrl);
      },
      () => {
        if (alive) setFailed(true);
      }
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uri]);

  if (!thumb || failed) return <>{fallback}</>;
  return <Image source={{ uri: thumb }} style={style} resizeMode="cover" />;
}
